export const heuristics = {
    manhattan: function (pos0, pos1) {
        var d1 = Math.abs(pos1.x - pos0.x);
        var d2 = Math.abs(pos1.y - pos0.y);
        return d1 + d2;
    },
    diagonal: function (pos0, pos1) {
        var D = 1;
        var D2 = Math.sqrt(2);
        var d1 = Math.abs(pos1.x - pos0.x);
        var d2 = Math.abs(pos1.y - pos0.y);
        return (D * (d1 + d2)) + ((D2 - (2 * D)) * Math.min(d1, d2));
    }
};

const defaults = {
    heuristic: heuristics.manhattan,
    closest: false
}

class AStarSearch {
    createPath(node) {
        var curr = node;
        var path = [];
        while (curr.astar.parent) {
            path.unshift({ x: curr.x - curr.astar.parent.x, y: curr.y - curr.astar.parent.y });
            curr = curr.astar.parent;
        }
        return path;
    }

    /**
     * http://github.com/bgrins/javascript-astar
     *
     * @param {Array} graph
     * @param {*} start
     * @param {*} end
     * @param {Object} opts
     * @returns {Array} An array of x,y positions that form a path.
     */
    search(graph, start, end, opts) {
        let options = Object.assign({}, defaults, opts);

        let heuristic = options.heuristic;
        let closest = options.closest;

        // If we're not searching for the closest and the target is a blocker. Return empty path.
        if (!closest && end.weight === 0) return [];

        graph.iterNodes(this.initNode);

        let dirtyNodes = [];
        let openHeap = this.getNewHeap();

        // Set the start node to be the closest if required.
        let closestNode = start;

        start.astar.h = heuristic(start, end);
        dirtyNodes.push(start);
        //graph.markDirty(start);

        openHeap.push(start);

        while (openHeap.size() > 0) {

            // Grab the lowest f(x) to process next.  Heap keeps this sorted for us.
            var currentNode = openHeap.pop();

            // End case -- result has been found, return the traced path.
            if (currentNode === end) {
                return this.createPath(currentNode);
            }

            // Normal case -- move currentNode from open to closed, process each of its neighbours.
            currentNode.astar.closed = true;

            // Find all neighbours for the current node.
            var neighbours = graph.getNeighbours(currentNode);

            for (var i = 0, il = neighbours.length; i < il; ++i) {
                var neighbour = neighbours[i];

                if (neighbour.astar.closed || neighbour.isBlocking()) {
                    // Not a valid node to process, skip to next neighbour.
                    continue;
                }

                // The g score is the shortest distance from start to current node.
                // We need to check if the path we have arrived at this neighbour is the shortest one we have seen yet.
                var gScore = currentNode.astar.g + neighbour.getCost(currentNode);
                var beenVisited = neighbour.astar.visited;

                if (!beenVisited || gScore < neighbour.astar.g) {

                    // Found an optimal (so far) path to this node.  Take score for node to see how good it is.
                    neighbour.astar.visited = true;
                    neighbour.astar.parent = currentNode;
                    neighbour.astar.h = neighbour.astar.h || heuristic(neighbour, end);
                    neighbour.astar.g = gScore;
                    neighbour.astar.f = neighbour.astar.g + neighbour.astar.h;
                    //graph.markDirty(neighbour);
                    dirtyNodes.push(neighbour);
                    if (closest) {
                        // If the neighbour is closer than the current closestNode or if it's equally close but has
                        // a cheaper path than the current closest node then it becomes the closest node
                        if (neighbour.astar.h < closestNode.astar.h || (neighbour.astar.h === closestNode.astar.h && neighbour.astar.g < closestNode.astar.g)) {
                            closestNode = neighbour;
                        }
                    }

                    if (!beenVisited) {
                        // Pushing to heap will put it in proper place based on the 'f' value.
                        openHeap.push(neighbour);
                    } else {
                        // Already seen the node, but since it has been rescored we need to reorder it in the heap
                        openHeap.rescoreElement(neighbour);
                    }
                }
            }
        }

        if (closest) return this.createPath(closestNode);
        return [];
    }

    initNode(node) {
        node.astar = {
            f: 0,
            g: 0,
            h: 0,
            visited: false,
            closed: false,
            parent: null
        };
    }

    getNewHeap() {
        return new BinaryHeap(function (node) {
            return node.astar.f;
        });
    }
}

/**
 * http://eloquentjavascript.net/1st_edition/appendix2.html
 */
class BinaryHeap {
    constructor(scoreFunc) {
        this.content = [];
        this.scoreFunc = scoreFunc;
    }

    push(element) {
        // Add the new element to the end of the array.
        this.content.push(element);

        // Allow it to sink down.
        this.sinkDown(this.content.length - 1);
    }

    pop() {
        // Store the first element so we can return it later.
        var result = this.content[0];
        // Get the element at the end of the array.
        var end = this.content.pop();
        // If there are any elements left, put the end element at the
        // start, and let it bubble up.
        if (this.content.length > 0) {
            this.content[0] = end;
            this.bubbleUp(0);
        }
        return result;
    }

    remove(node) {
        var i = this.content.indexOf(node);

        // When it is found, the process seen in 'pop' is repeated
        // to fill up the hole.
        var end = this.content.pop();

        if (i !== this.content.length - 1) {
            this.content[i] = end;

            if (this.scoreFunc(end) < this.scoreFunc(node)) {
                this.sinkDown(i);
            } else {
                this.bubbleUp(i);
            }
        }
    }

    size() {
        return this.content.length;
    }

    rescoreElement(node) {
        this.sinkDown(this.content.indexOf(node));
    }

    sinkDown(n) {
        // Fetch the element that has to be sunk.
        var element = this.content[n];

        // When at 0, an element can not sink any further.
        while (n > 0) {

            // Compute the parent element's index, and fetch it.
            var parentN = ((n + 1) >> 1) - 1;
            var parent = this.content[parentN];
            // Swap the elements if the parent is greater.
            if (this.scoreFunc(element) < this.scoreFunc(parent)) {
                this.content[parentN] = element;
                this.content[n] = parent;
                // Update 'n' to continue at the new position.
                n = parentN;
            }
            // Found a parent that is less, no need to sink any further.
            else {
                break;
            }
        }
    }

    bubbleUp(n) {
        // Look up the target element and its score.
        var length = this.content.length;
        var element = this.content[n];
        var elemScore = this.scoreFunc(element);

        while (true) {
            // Compute the indices of the child elements.
            var child2N = (n + 1) << 1;
            var child1N = child2N - 1;
            // This is used to store the new position of the element, if any.
            var swap = null;
            var child1Score;
            // If the first child exists (is inside the array)...
            if (child1N < length) {
                // Look it up and compute its score.
                var child1 = this.content[child1N];
                child1Score = this.scoreFunc(child1);

                // If the score is less than our element's, we need to swap.
                if (child1Score < elemScore) {
                    swap = child1N;
                }
            }

            // Do the same checks for the other child.
            if (child2N < length) {
                var child2 = this.content[child2N];
                var child2Score = this.scoreFunc(child2);
                if (child2Score < (swap === null ? elemScore : child1Score)) {
                    swap = child2N;
                }
            }

            // If the element needs to be moved, swap it, and continue.
            if (swap !== null) {
                this.content[n] = this.content[swap];
                this.content[swap] = element;
                n = swap;
            }
            // Otherwise, we are done.
            else {
                break;
            }
        }
    }
}

export let AStar = new AStarSearch();
